import { getValidatedLimitParam, getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import http from '../config/http-service';
import type { UnmatchedMasterProductDemandListResponse } from './types';
import { restaurantInquiriesEndpoint } from './use-create-restaurant-inquiry-api';

const endpoint = `${restaurantInquiriesEndpoint}/master-products/unmatched-demand`;
export const unmatchedMasterProductDemandEndpoint = endpoint;

type TData = UnmatchedMasterProductDemandListResponse;
type TError = AxiosError;
type TQueryKey = [
  typeof endpoint,
  {
    page: TParam;
    limit: TParam;
    status: TParam;
    category: TParam;
    origin: TParam;
    sort: TParam;
  }
];
type Options = Omit<UseQueryOptions<TData, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetUnmatchedMasterProductDemandApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const page = getValidatedPageParam();
  const limit = getValidatedLimitParam();
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { status, category, origin, sort } = getPageParams();

  const queryFn = async () => {
    try {
      const { data } = await http.get(endpoint, { params: { page, limit, status, category, origin, sort } });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper<TData, TError, TData, TQueryKey>(
    [endpoint, { page, limit, status, category, origin, sort }],
    queryFn,
    {
      enabled: isLoggedIn,
      keepPreviousData: true,
      ...options,
    }
  );
}
